import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faFilter } from '@fortawesome/free-solid-svg-icons';
import { IoMdClose } from 'react-icons/io';
import axios from 'axios';
import { toast } from 'react-toastify';
import DynamicTable from '../../../components/DynamicTable/DynamicTable';
import FilledButton from '../../../components/Buttons/FilledButton';
import CustomPagination from '../../../components/Pagination/CustomPagination';
import { STAGE_TRANSITION_RULES_DATA } from '../../../config/stage_transition_rules';
import RuleBuilderDrawer from '../components/RuleBuilderDrawer/RuleBuilderDrawer';
import TransitionPreviewModal from '../components/TransitionPreviewModal/TransitionPreviewModal';
import useDebounce from '../../../hooks/useDebounce';
import '../../../components/StageChangeModal/StageChangeModal.css';
import '../../Reports/FilterModal/FilterModal.css';
import '../../ApplicationsDashboard/ApplicationsDashboard.css';
import './RuleListPage.css';
import { getAuthToken } from "../../../utils/auth";

const PAGE_SIZE = 10;

const EMPTY_FILTERS = { direction: '', is_active: '' };

const FILTER_FIELDS = [
    {
        key: 'direction',
        label: 'Direction',
        options: [
            { value: '', label: 'All' },
            { value: 'FORWARD', label: 'FORWARD' },
            { value: 'BACKWARD', label: 'BACKWARD' },
        ],
    },
    {
        key: 'is_active',
        label: 'Status',
        options: [
            { value: '', label: 'All' },
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' },
        ],
    },
];

const RuleListPage = () => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRule, setSelectedRule] = useState(null);
    const [showBuilder, setShowBuilder] = useState(false);

    const [testTarget, setTestTarget] = useState(null); // rule row pending a test-run
    const [testApplicationId, setTestApplicationId] = useState('');
    const [testScopedEntityId, setTestScopedEntityId] = useState('');
    const [runningPreview, setRunningPreview] = useState(null); // rule + params once confirmed

    const [searchInput, setSearchInput] = useState('');
    const search = useDebounce(searchInput, 500);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const searchInputRef = useRef(null);

    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS); // edited inside the drawer, applied on "Apply Filters"
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);
    const [activeFilterTab, setActiveFilterTab] = useState(FILTER_FIELDS[0].key);

    const activeFilterCount = (search ? 1 : 0) + (filters.direction ? 1 : 0) + (filters.is_active ? 1 : 0);

    // The backend list endpoint has no server-side pagination yet (it returns
    // every active + legacy rule in one call), so this paginates client-side
    // over the already-fetched (already server-filtered) list.
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(rules.length / PAGE_SIZE));
    const pagedRules = rules.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const authToken = useMemo(() => {
        // const tokens = JSON.parse(sessionStorage.getItem('tokens')) || null;
        // return tokens?.token || null;
        return getAuthToken();
    }, []);

    const headers = { Authorization: `Bearer ${authToken}`, 'Content-type': 'application/json' };

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/application-stage/get-stage-transition-rules`,
                {
                    headers,
                    params: {
                        direction: filters.direction || undefined,
                        is_active: filters.is_active || undefined,
                        search: search || undefined,
                    },
                }
            );

            const rows = (resp?.data?.data || []).map((rule) => ({
                ...rule,
                id: rule.stage_transition_id,
                from_stage_name: rule.fromStage?.stage_name,
                to_stage_name: rule.toStage?.stage_name,
            }));

            setRules(rows);
            setCurrentPage((prev) => Math.min(prev, Math.max(1, Math.ceil(rows.length / PAGE_SIZE))));
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load transition rules');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, search]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    // Any filter/search change should land back on page 1, not stay on
    // whatever page the previous (differently-filtered) result set had.
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, search]);

    const handleClearFilters = () => {
        setSearchInput('');
        setFilters(EMPTY_FILTERS);
        setDraftFilters(EMPTY_FILTERS);
        setCurrentPage(1);
    };

    const handleAction = async (action, row) => {
        if (action === 'edit') {
            setSelectedRule(row);
            setShowBuilder(true);
        } else if (action === 'toggle-active') {
            try {
                await axios.put(
                    `${process.env.REACT_APP_API_BASE_URL}/application-stage/update-transition-rule/${row.stage_transition_id}`,
                    { is_active: !row.is_active },
                    { headers }
                );
                toast.success(`Rule ${row.is_active ? 'deactivated' : 'activated'}`);
                fetchRules();
            } catch (error) {
                toast.error(error?.response?.data?.message || 'Failed to update rule');
            }
        } else if (action === 'test') {
            setTestApplicationId('');
            setTestScopedEntityId('');
            setTestTarget(row);
        }
    };

    const activeField = FILTER_FIELDS.find((f) => f.key === activeFilterTab) || FILTER_FIELDS[0];

    return (
        <div>
            <Row className="g-2 align-items-center mb-3">
                <Col className="rule-list-header">
                    <h5 className="mb-0">Transition Rules</h5>
                    <small className="text-muted">
                        Configure which From → To stage pairs are allowed and what happens on each transition.
                    </small>
                </Col>
                <Col md="auto" className="ms-auto d-flex align-items-center gap-2">
                <div className="ruleListActionsBar">
                    <div className="filterButtons">
                        <div className={`expandableSearchContainer ${isSearchOpen ? 'expanded' : ''}`}>
                            <div className="expandableSearchInputWrapper">
                                <FontAwesomeIcon icon={faSearch} className="searchIconInside" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    name="rule_search"
                                    value={searchInput}
                                    placeholder="Search by rule name / label..."
                                    onChange={(e) => setSearchInput(e.target.value)}
                                />
                                {searchInput && (
                                    <button className="searchClearBtn" title="Clear Search" onClick={() => setSearchInput('')}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </div>
                            <button
                                className={`searchToggleBtn ${isSearchOpen ? 'active' : ''}`}
                                onClick={() => {
                                    const next = !isSearchOpen;
                                    setIsSearchOpen(next);
                                    if (next) setTimeout(() => searchInputRef.current?.focus(), 150);
                                }}
                                title={isSearchOpen ? 'Close Search' : 'Search Rules'}
                            >
                                <FontAwesomeIcon icon={faSearch} />
                            </button>
                        </div>

                        <button
                            className={`allFiltersBtn ${isSearchOpen ? 'iconOnly' : ''}`}
                            onClick={() => {
                                setDraftFilters(filters);
                                setActiveFilterTab(FILTER_FIELDS[0].key);
                                setShowFilterDrawer(true);
                            }}
                            title="All Filters"
                        >
                            <FontAwesomeIcon icon={faFilter} />
                            {!isSearchOpen && <span>All Filters</span>}
                            {activeFilterCount > 0 && <span className="activeFilterCount">{activeFilterCount}</span>}
                        </button>

                        {activeFilterCount > 0 && (
                            <button
                                className={`clearFiltersBtn ${isSearchOpen ? 'iconOnly' : ''}`}
                                onClick={handleClearFilters}
                                title="Clear Filters"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {!isSearchOpen && <span>Clear Filters</span>}
                            </button>
                        )}
                    </div>

                    <FilledButton
                        title="+ Create Rule"
                        handleClick={() => {
                            setSelectedRule(null);
                            setShowBuilder(true);
                        }}
                    />
                    </div>
                </Col>
            </Row>

            {showFilterDrawer &&
                ReactDOM.createPortal(
                    <>
                        <div className="filterPopoverBackdrop" onClick={() => setShowFilterDrawer(false)} />
                        <div className="filterPopoverPanel" onClick={(e) => e.stopPropagation()}>
                            <div className="filterModalHeader">
                                <h2>Filter Transition Rules</h2>
                                <button onClick={() => setShowFilterDrawer(false)} className="filterModalCloseBtn">
                                    <IoMdClose size={20} />
                                </button>
                            </div>

                            <div className="filterModalBody">
                                <div className="filterDrawerLayout">
                                    <div className="filterNavSidebar">
                                        {FILTER_FIELDS.map((field) => {
                                            const hasVal = Boolean(draftFilters[field.key]);
                                            const isActive = activeFilterTab === field.key;
                                            return (
                                                <button
                                                    key={field.key}
                                                    className={`filterNavItem ${isActive ? 'active' : ''}`}
                                                    onClick={() => setActiveFilterTab(field.key)}
                                                >
                                                    <span className="filterNavLabel">{field.label}</span>
                                                    {hasVal && <span className="filterNavBadge" />}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="filterContentPanel">
                                        <div className="filterPanelSection">
                                            <div className="filterPanelTitle">{activeField.label}</div>
                                            <select
                                                className="form-select"
                                                value={draftFilters[activeField.key]}
                                                onChange={(e) =>
                                                    setDraftFilters((p) => ({ ...p, [activeField.key]: e.target.value }))
                                                }
                                            >
                                                {activeField.options.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="filterModalFooter">
                                <button
                                    className="clearAllBtn"
                                    onClick={() => {
                                        setDraftFilters(EMPTY_FILTERS);
                                        setFilters(EMPTY_FILTERS);
                                    }}
                                >
                                    Clear Filters
                                </button>
                                <button
                                    className="applyFiltersBtn"
                                    onClick={() => {
                                        setFilters(draftFilters);
                                        setShowFilterDrawer(false);
                                    }}
                                >
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    </>,
                    document.body
                )}

            <div className="dynamic-table-container">
                <DynamicTable columns={STAGE_TRANSITION_RULES_DATA} data={loading ? [] : pagedRules} onAction={handleAction} />
            </div>

            {rules.length > 0 && (
                <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            )}

            {showBuilder && (
                <RuleBuilderDrawer
                    rule={selectedRule}
                    onClose={() => setShowBuilder(false)}
                    onSaved={() => {
                        setShowBuilder(false);
                        fetchRules();
                    }}
                />
            )}

            {testTarget && (
                <Modal show onHide={() => setTestTarget(null)} centered className="test-transition-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>Test: {testTarget.label || `${testTarget.from_stage_name} → ${testTarget.to_stage_name}`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Application ID</Form.Label>
                            <Form.Control
                                type="text"
                                value={testApplicationId}
                                onChange={(e) => setTestApplicationId(e.target.value)}
                                placeholder="Paste an application_id to preview/execute against"
                            />
                        </Form.Group>
                        {testTarget.scope === 'TRANCHE' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Tranche ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={testScopedEntityId}
                                    onChange={(e) => setTestScopedEntityId(e.target.value)}
                                    placeholder="Required for a TRANCHE-scoped rule"
                                />
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="stage-change-btn btn-outline" onClick={() => setTestTarget(null)}>
                            Cancel
                        </button>
                        <button
                            className="stage-change-btn btn-solid"
                            onClick={() => {
                                if (!testApplicationId) {
                                    toast.error('Application ID is required');
                                    return;
                                }
                                setRunningPreview({
                                    applicationId: testApplicationId,
                                    stageTransitionId: testTarget.stage_transition_id,
                                    scopedEntityId: testScopedEntityId || undefined,
                                });
                                setTestTarget(null);
                            }}
                        >
                            Preview
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {runningPreview && (
                <TransitionPreviewModal
                    applicationId={runningPreview.applicationId}
                    stageTransitionId={runningPreview.stageTransitionId}
                    scopedEntityId={runningPreview.scopedEntityId}
                    onClose={() => setRunningPreview(null)}
                    onExecuted={() => fetchRules()}
                />
            )}
        </div>
    );
};

export default RuleListPage;
